/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */
import React, { useState } from 'react';
import logo from '../../assets/LogoWhiteCropped.png';

const BACKEND_URL = 'https://back-end-x7tx.onrender.com';

const Unsubscribe = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'already' | 'error' | 'noemail'

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setStatus('noemail');
      return;
    }
    setStatus('loading');

    try {
      const res = await fetch(`${BACKEND_URL}/api/demos/unsubscribe?email=${encodeURIComponent(email.trim().toLowerCase())}`);
      const data = await res.json();
      if (data.success) {
        setStatus(data.alreadyRemoved ? 'already' : 'success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src={logo} alt="Lauft" style={styles.logo} />

        {status === 'idle' || status === 'loading' || status === 'noemail' ? (
          <>
            <h1 style={styles.title}>Unsubscribe</h1>
            <p style={styles.message}>
              Enter your email below and we'll remove you from our list.
            </p>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); if (status === 'noemail') setStatus('idle'); }}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              placeholder="your@email.com"
              style={{
                ...styles.input,
                borderColor: status === 'noemail' ? 'rgba(220,53,69,0.6)' : 'rgba(255,255,255,0.15)'
              }}
              disabled={status === 'loading'}
            />
            {status === 'noemail' && (
              <p style={styles.validationError}>Please enter a valid email address.</p>
            )}
            <button
              onClick={handleSubmit}
              disabled={status === 'loading'}
              style={{
                ...styles.button,
                opacity: status === 'loading' ? 0.5 : 1,
                cursor: status === 'loading' ? 'not-allowed' : 'pointer'
              }}
            >
              {status === 'loading' ? 'Removing...' : 'Unsubscribe'}
            </button>
          </>
        ) : (
          <>
            <div style={styles.iconCircle}>
              <span style={styles.icon}>
                {status === 'error' ? '✕' : '✓'}
              </span>
            </div>
            <h1 style={styles.title}>
              {status === 'success' && "You've been unsubscribed"}
              {status === 'already' && 'Already unsubscribed'}
              {status === 'error' && 'Something went wrong'}
            </h1>
            <p style={styles.message}>
              {status === 'success' && "You've been removed from our list and won't receive these emails anymore."}
              {status === 'already' && "You're not on our list — no further action needed."}
              {status === 'error' && "We couldn't process your request. Please contact us at team@lauft.app."}
            </p>
            <a href="https://www.lauft.app/demo-preview" style={styles.link}>
              Back to Lauft
            </a>
          </>
        )}
      </div>

      <p style={styles.footer}>
        © {new Date().getFullYear()} Lauft Inc. · <a href="mailto:team@lauft.app" style={styles.footerLink}>team@lauft.app</a>
      </p>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: "'Montserrat', 'Roboto', sans-serif"
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    padding: '48px 40px',
    maxWidth: '420px',
    width: '100%',
    textAlign: 'center'
  },
  logo: {
    height: '28px',
    marginBottom: '36px',
    opacity: 0.9
  },
  iconCircle: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.15)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px'
  },
  icon: {
    fontSize: '24px',
    color: '#ffffff'
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 12px'
  },
  message: {
    fontSize: '15px',
    color: 'rgba(255,255,255,0.55)',
    lineHeight: '1.6',
    margin: '0 0 24px'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.15)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    color: '#ffffff',
    fontSize: '15px',
    fontFamily: "'Montserrat', 'Roboto', sans-serif",
    outline: 'none',
    marginBottom: '16px',
    boxSizing: 'border-box'
  },
  validationError: {
    color: 'rgba(220,53,69,0.9)',
    fontSize: '13px',
    marginBottom: '12px',
    marginTop: '-8px',
    textAlign: 'left'
  },
  button: {
    width: '100%',
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
    border: 'none',
    padding: '13px 28px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    letterSpacing: '0.3px',
    transition: 'opacity 0.2s'
  },
  link: {
    display: 'inline-block',
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
    padding: '12px 28px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '700',
    textDecoration: 'none',
    letterSpacing: '0.3px'
  },
  footer: {
    marginTop: '32px',
    fontSize: '13px',
    color: 'rgba(255,255,255,0.25)'
  },
  footerLink: {
    color: 'rgba(255,255,255,0.35)',
    textDecoration: 'none'
  }
};

export default Unsubscribe;